import { format, isSameDay } from "date-fns";

import { CURRENCY, Currency, DATE_FORMAT, LOCALE, Locale } from "@/constants";

import { ensureIsDate } from "./ensureIsDate";

export const formatDateForApi = (date: Date) => format(date, DATE_FORMAT.API);

export const formatDateRange = (
  start: Date | string | undefined,
  end: Date | string | undefined,
  formatter: (d: Date) => string
) => {
  const startAsDate = ensureIsDate(start, undefined);
  const endAsDate = ensureIsDate(end, undefined);

  // it's a span. Format it properly:
  if (startAsDate && endAsDate) {
    if (isSameDay(startAsDate, endAsDate)) {
      return formatter(startAsDate);
    }

    return `${formatter(startAsDate)} - ${formatter(endAsDate)}`;
  }
  // Maybe one of them is defined, format that one
  if (startAsDate) return formatter(startAsDate);
  if (endAsDate) return formatter(endAsDate);

  // Nope. Return an empty string.
  return "";
};

interface AbbreviateNumberConfig {
  thousands: string;
  millions: string;
  billions: string;
}

const ABBREVIATE_NUMBER_CONFIG: Record<Locale, AbbreviateNumberConfig> = {
  [LOCALE.ENGLISH]: { thousands: "K", millions: "M", billions: "B" },
  [LOCALE.SWEDISH]: { thousands: " t", millions: " mn", billions: " md" },
} as const;

type Formatter = (n: number) => string;

export function formatNumberAbbreviated(
  value: number,
  language: Locale,
  formatter: Formatter
): string {
  const { thousands, millions, billions } = ABBREVIATE_NUMBER_CONFIG[language];

  if (value >= 1_000_000_000) {
    return `${formatter(value / 1_000_000_000)}${billions}`;
  } else if (value >= 1_000_000) {
    return `${formatter(value / 1_000_000)}${millions}`;
  } else if (value >= 10_000) {
    return `${formatter(value / 1_000)}${thousands}`;
  } else if (value >= 1_000) {
    // If a value in the thousands has no decimals it's as short to just show the whole thing
    const hasDecimals = value % 1 !== 0;

    if (hasDecimals) {
      return `${formatter(value / 1_000)}${thousands}`;
    }
    return `${formatter(value)}`;
  } else {
    return `${formatter(value)}`;
  }
}

interface AbbreviateCurrencyConfig {
  less?: string;
  thousands: string;
  millions: string;
  billions: string;
  suffix?: string;
  prefix?: string;
}

const ABBREVIATE_CURRENCY_CONFIG: Record<
  Locale,
  Record<Currency, AbbreviateCurrencyConfig>
> = {
  [LOCALE.ENGLISH]: {
    [CURRENCY.SEK]: {
      thousands: "K",
      millions: "M",
      billions: "B",
      prefix: "SEK ",
    },
    [CURRENCY.NOK]: {
      thousands: "K",
      millions: "M",
      billions: "B",
      prefix: "NOK ",
    },
    [CURRENCY.USD]: {
      thousands: "K",
      millions: "M",
      billions: "B",
      prefix: "$",
    },
    [CURRENCY.EUR]: {
      thousands: "K",
      millions: "M",
      billions: "B",
      prefix: "€",
    },
  },
  [LOCALE.SWEDISH]: {
    [CURRENCY.SEK]: {
      less: " kr",
      thousands: " tkr",
      millions: " mnkr",
      billions: " mdkr",
    },
    [CURRENCY.NOK]: {
      less: " NOK",
      thousands: " t NOK",
      millions: " mn NOK",
      billions: " md NOK",
    },
    [CURRENCY.USD]: {
      thousands: " t",
      millions: " mn",
      billions: " md",
      suffix: " US$",
    },
    [CURRENCY.EUR]: {
      thousands: " t",
      millions: " mn",
      billions: " md",
      suffix: " €",
    },
  },
} as const;

/** Formats an abbreviates a currency, example 5 000 000 => 5 mnkr */
export function formatCurrencyAbbreviated(
  value: number,
  language: Locale,
  currency: Currency,
  formatter: Formatter
): string {
  const { thousands, millions, billions, prefix, suffix, less } =
    ABBREVIATE_CURRENCY_CONFIG[language][currency];

  let result = "";

  if (value >= 1_000_000_000) {
    result = `${formatter(value / 1_000_000_000)}${billions}`;
  } else if (value >= 1_000_000) {
    result = `${formatter(value / 1_000_000)}${millions}`;
  } else if (value >= 10_000) {
    result = `${formatter(value / 1_000)}${thousands}`;
  } else if (value >= 1_000) {
    // If a value in the thousands with 5 digits has no decimals it's as short to just show the whole thing
    const hasDecimals = value % 1 !== 0;

    if (hasDecimals) {
      result = `${formatter(value / 1_000)}${thousands}`;
    } else {
      result = `${formatter(value)}${less || ""}`;
    }
  } else {
    result = `${formatter(value)}${less || ""}`;
  }
  return `${prefix || ""}${result}${suffix || ""}`;
}
